import React, { useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    BoxProps,
    Grid,
    MenuItem,
    IconButton,
    Menu,
    Divider,
    ListItemIcon,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    MoreVert as MoreIcon,
    FilterList as FilterListIcon,
} from '@mui/icons-material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Typography from '@mui/material/Typography';
import FilterDialog from '../components/filter/FilterDialog';
import { SttButtonDelete, SttButtonNew, SttFormSelect } from './all';
import FilterFields from '../components/filter/FilterFields';
import { useToggle } from '../lib/hooks';
import { usePermission } from '../lib/usePermission';
import { PermissionRequirement } from '../lib/permissions';

const FIELD_CHECKED_VALUES = ['check', 'switch'];
const RENDER_AFTER_CHANGE_FILTERS = ['select', 'check', 'switch'];

type FilterField = any;

// MenuItem type for menu prop
type MenuItemType = {
    show?: boolean;
    caption?: string;
    onClick?: () => void;
    icon?: ReactNode;
    divider?: boolean;
    disabled?: boolean;
};

// MainAction type
type MainActionType = {
    type?: 'create' | 'delete' | 'select' | 'component';
    onClick?: () => void;
    caption?: string;

    options?: any[];
    selected?: string | number;
    onChange?: (value: string | number) => void;
    component?: ReactNode;
    /** Permiso v8 que hace falta para ver la acción (p. ej. `<módulo>.create`). */
    permission?: PermissionRequirement;
};

// IconButton type
type IconButtonType = {
    icon: ReactNode;
    onClick: () => void;
    caption?: string;
    disabled?: boolean;
};

// Props type
type SttTopFilteringControlsProps = {
    fields?: FilterField[];
    extraFields?: FilterField[];
    mainAction?: MainActionType | null;
    menu?: MenuItemType[];
    menuIcon?: ReactNode;
    filterButton?: { onClick: () => void };
    downloadButton?: { onClick: () => void };
    iconButtons?: IconButtonType[];
    trashAction?: boolean;

    onFilter?: (filters: any) => void;
    skipFirstFiltering?: boolean;
} & Omit<BoxProps, 'onFilter'>;

const fieldCheckedToValue = (field: any) =>
    FIELD_CHECKED_VALUES.includes(field.type ?? '')
        ? field.checked
        : field.value;

export function SttTopFilteringControls({
    fields = [],
    extraFields = [],
    mainAction: mainActionProp,
    menu,
    menuIcon,
    filterButton,
    downloadButton,
    iconButtons,
    trashAction,
    onFilter,
    skipFirstFiltering = false,
    ...rest
}: SttTopFilteringControlsProps) {
    const { t } = useTranslation();
    const [buttonMenuDOMiObject, setButtonMenuDOMObject] =
        useState<HTMLElement | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const { can } = usePermission();
    const mainAction =
        mainActionProp?.permission && !can(mainActionProp.permission)
            ? undefined
            : mainActionProp;

    const [internalFields, setInternalFields] = useState<any[]>(fields);

    const [internalExtraFields, setInternalExtraFilters] =
        useState<any[]>(extraFields);
    const [trash, toggleTrash] = useToggle(() => {
        doFiltering();
    }, false);

    // Setup relative sizes of the controls
    const extra =
        extraFields ||
        filterButton ||
        downloadButton ||
        menu ||
        trashAction ||
        iconButtons;
    let sizes;
    if (fields && extra) {
        // Everything's up
        sizes = {
            fields: {
                xs: 12,
                md: 8,
                lg: 10,
            },
            extra: {
                xs: 12,
                md: 4,
                lg: 2,
            },
        };
    } else {
        sizes = {
            fields: {
                xs: 12,
            },
        };
    }

    function doFiltering() {
        const updatedFilters: any = {};
        if (internalFields) {
            internalFields.forEach((field: any) => {
                updatedFilters[field.name] = fieldCheckedToValue(field);
            });
        }

        if (internalExtraFields) {
            internalExtraFields.forEach((field: any) => {
                // Chapuza para FromSelectPeriod porque no tiene 'name'
                if (field.selectName && field.nameIni && field.nameEnd) {
                    updatedFilters[field.selectName] = field.selectValue;
                    updatedFilters[field.nameIni] = field.valIni;
                    updatedFilters[field.nameEnd] = field.valEnd;
                } else {
                    updatedFilters[field.name] = fieldCheckedToValue(field);
                }
            });
        }
        if (trashAction) {
            updatedFilters.trash = trash;
        }

        onFilter?.(updatedFilters);
    }

    useEffect(() => {
        if (onFilter && !skipFirstFiltering) {
            doFiltering();
        }
    }, []);

    useEffect(() => {
        if (
            extraFields &&
            extraFields.length > 0 &&
            internalExtraFields &&
            internalExtraFields.length <= 0
        ) {
            setInternalExtraFilters(extraFields);
        }
    }, [extraFields]);

    function doFilteringParams(fieldsParams: any[], extraFieldsParams: any[]) {
        const updatedFilters: any = {};
        if (fieldsParams) {
            fieldsParams.forEach((field: any) => {
                updatedFilters[field.name] = fieldCheckedToValue(field);
            });
        }

        if (extraFieldsParams) {
            extraFieldsParams.forEach((field: any) => {
                updatedFilters[field.name] = fieldCheckedToValue(field);
            });
        }

        if (trashAction) {
            updatedFilters.trash = trash;
        }

        onFilter?.(updatedFilters);
    }

    function onChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: any;
    }) {
        if (!name) return;
        const updatedFields = [...internalFields];
        const updatedExtraFields = [...internalExtraFields];
        const internalFieldsIndexToChange = internalFields.findIndex(
            (field: any) => field.name === name,
        );
        const internalExtraFieldsIndexToChange = internalExtraFields.findIndex(
            (field: any) => field.name === name,
        );

        if (internalFieldsIndexToChange >= 0) {
            if (
                FIELD_CHECKED_VALUES.includes(
                    updatedFields[internalFieldsIndexToChange].type ?? '',
                )
            ) {
                updatedFields[internalFieldsIndexToChange].checked = value;
            } else {
                updatedFields[internalFieldsIndexToChange].value = value;
            }

            setInternalFields(updatedFields);
        }

        if (internalExtraFieldsIndexToChange >= 0) {
            if (
                FIELD_CHECKED_VALUES.includes(
                    updatedExtraFields[internalExtraFieldsIndexToChange].type ??
                        '',
                )
            ) {
                updatedExtraFields[internalExtraFieldsIndexToChange].checked =
                    value;
            } else {
                updatedExtraFields[internalExtraFieldsIndexToChange].value =
                    value;
            }

            setInternalExtraFilters(updatedExtraFields);
        }

        // Esto es una chapuza mientras no migremos a react-hook-forms este componente para que el selector
        // haga la petición de filtrado cuando se modifique su valor ya que no podemos esperar a que la
        // actualización asincrona del estado de internalFields termine y luego llamar a onFilter.
        if (
            internalFieldsIndexToChange >= 0 &&
            RENDER_AFTER_CHANGE_FILTERS.includes(
                updatedFields[internalFieldsIndexToChange].type ?? '',
            )
        ) {
            doFilteringParams(updatedFields, updatedExtraFields);
        }

        // Chapuza para FromSelectPeriod porque no tiene 'name'
        if (
            internalFieldsIndexToChange < 0 &&
            internalExtraFieldsIndexToChange < 0
        ) {
            const selectIndex = internalExtraFields.findIndex(
                (field: FilterField) => field.selectName === name,
            );

            if (selectIndex >= 0) {
                updatedExtraFields[selectIndex].selectValue = value;
                setInternalExtraFilters(updatedExtraFields);
                return;
            }

            const dateIniIndex = internalExtraFields.findIndex(
                (field: FilterField) => field.nameIni === name,
            );

            if (dateIniIndex >= 0) {
                updatedExtraFields[dateIniIndex].valIni = value;
                setInternalExtraFilters(updatedExtraFields);
                return;
            }

            const dateEndIndex = internalExtraFields.findIndex(
                (field: FilterField) => field.nameEnd === name,
            );

            if (dateEndIndex >= 0) {
                updatedExtraFields[dateEndIndex].valEnd = value;
                setInternalExtraFilters(updatedExtraFields);
            }
        }
    }

    function renderMainAction() {
        let content;

        if (mainAction) {
            switch (mainAction.type) {
                case 'create':
                    content = (
                        <SttButtonNew
                            caption={mainAction.caption}
                            onClick={mainAction.onClick ?? (() => {})}
                        />
                    );
                    break;
                case 'delete':
                    content = <SttButtonDelete onClick={mainAction.onClick} />;
                    break;
                case 'select':
                    content = (
                        <SttFormSelect
                            name=""
                            caption=""
                            options={mainAction.options}
                            onChange={({ value }: any) =>
                                mainAction.onChange?.(value)
                            }
                            defVal={mainAction.selected}
                        />
                    );
                    break;
                case 'component':
                    content = mainAction.component;
                    break;
                default:
            }
        }
        return content;
    }

    function renderDefaultFields(gridSizes: any) {
        if (internalFields) {
            let content;

            // If we only specify a single text field to filter by default, we turn it into a search form text field
            if (
                internalFields.length === 1 &&
                internalFields[0].type === 'text'
            ) {
                content = (
                    <FilterFields
                        spacing={3}
                        onChange={onChange}
                        fields={[
                            {
                                ...internalFields[0],
                                type: 'search',
                                onEnterKey: () => doFiltering(),
                                xs: internalFields[0].xs ?? 12,
                                sm: internalFields[0].sm ?? 12,
                                md: internalFields[0].md ?? 12,
                                lg: internalFields[0].lg ?? 8,
                            },
                        ]}
                        action={renderMainAction()}
                    />
                );
            } else {
                content = (
                    <FilterFields
                        spacing={3}
                        onChange={onChange}
                        fields={internalFields.map((field: FilterField) =>
                            ['search', 'text'].includes(field.type ?? '')
                                ? { ...field, onEnterKey: doFiltering }
                                : field,
                        )}
                        action={renderMainAction()}
                    />
                );
            }

            return <Grid size={gridSizes}>{content}</Grid>;
        }
    }

    function renderExtra(gridSizes: any) {
        // Extra components container should grow to take all available space (but should it really?)
        const mustGrow = true; // onToggleTrash && !(extraFields || filterButton || downloadButton || menu)

        return (
            <Grid size={gridSizes} style={mustGrow ? { flexGrow: 1 } : {}}>
                <Box display="flex" justifyContent="flex-end">
                    {/* --- Trash --- */}
                    {trashAction && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={
                                        undefined /* We don't care about this value because the switch has its own state and this will always be false first */
                                    }
                                    onChange={(ev: any) =>
                                        toggleTrash(ev.target.checked)
                                    }
                                />
                            }
                            label={t('Trash')}
                        />
                    )}

                    {/* --- Popup filter --- */}
                    {!!internalExtraFields.length && (
                        <IconButton
                            id="filter-button"
                            onClick={() => setDialogOpen(true)}
                        >
                            <FilterListIcon />
                        </IconButton>
                    )}

                    {filterButton /* Solo se usa en Report y Mship; TODO: Unificar esos 2 componentes y eliminar esto */ && (
                        <IconButton
                            id="filter-button"
                            onClick={filterButton.onClick}
                        >
                            <FilterListIcon />
                        </IconButton>
                    )}

                    {downloadButton /* Solo se usa en Report y Mship; TODO: Unificar esos 2 componentes y eliminar esto */ && (
                        <IconButton onClick={downloadButton.onClick}>
                            <CloudDownloadIcon />
                        </IconButton>
                    )}

                    <Box display="flex" alignItems="center">
                        {iconButtons?.map((item: any, idx: any) => (
                            <React.Fragment key={idx}>
                                <IconButton
                                    onClick={item.onClick}
                                    disabled={item.disabled}
                                >
                                    {item.icon}
                                </IconButton>
                                {item.caption && (
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        gutterBottom
                                    >
                                        {item.caption}
                                    </Typography>
                                )}
                            </React.Fragment>
                        ))}
                    </Box>

                    {menu && (
                        <>
                            <IconButton
                                onClick={(ev: any) =>
                                    setButtonMenuDOMObject(ev.currentTarget)
                                }
                            >
                                {menuIcon || <MoreIcon />}
                            </IconButton>
                            <Menu
                                anchorEl={buttonMenuDOMiObject}
                                open={buttonMenuDOMiObject !== null}
                                onClose={() => setButtonMenuDOMObject(null)}
                            >
                                {menu?.map((item: any, idx: any) => (
                                    <div key={idx}>
                                        {item.divider && <Divider />}
                                        <MenuItem
                                            onClick={() => {
                                                if (
                                                    typeof item.onClick ===
                                                    'function'
                                                ) {
                                                    item.onClick();
                                                }
                                                setButtonMenuDOMObject(null);
                                            }}
                                            disabled={item.disabled}
                                        >
                                            {item.icon && (
                                                <ListItemIcon>
                                                    {item.icon}
                                                </ListItemIcon>
                                            )}
                                            {item.caption}
                                        </MenuItem>
                                    </div>
                                ))}
                            </Menu>
                        </>
                    )}
                </Box>
            </Grid>
        );
    }

    return (
        <>
            <Box {...rest}>
                <Grid container spacing={3}>
                    {renderDefaultFields(sizes.fields)}

                    {mainAction && !internalFields && (
                        <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2, xl: 1 }}>
                            {renderMainAction()}
                        </Grid>
                    )}

                    {extra && renderExtra(sizes.extra)}
                </Grid>
            </Box>

            {!!internalExtraFields.length && (
                <FilterDialog
                    title={t('Filter')}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    fields={internalExtraFields}
                    onChange={onChange}
                    onApply={() => {
                        doFiltering();
                        setDialogOpen(false);
                    }}
                />
            )}
        </>
    );
}
